import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { BidderStream, OutcomeAI, FeeTransparencyMock, ChannelMatrix } from "@/components/mocks/ProductMocks";

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 3]);

  return (
    <section ref={ref} className="relative overflow-hidden border-b border-[#D8E2E6]" data-testid="home-hero">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: "radial-gradient(60% 50% at 75% 30%, rgba(50,162,197,0.10) 0%, rgba(50,162,197,0) 70%)" }} />
      <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow mb-6"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Omnichannel DSP · A Xyle Company
          </motion.p>
          <h1 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.1}>Every impression.</MaskedLine>
            <MaskedLine delay={0.22}>Every dollar.</MaskedLine>
            <MaskedLine delay={0.34}>
              Total <span className="accent-word">transparency.</span>
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-xl"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            data-testid="hero-subheadline"
          >
            RresponsIQ is the enterprise DSP that charges media at cost plus one disclosed fee — and hands you the log-level data to prove it. Seven channels. One workflow. Zero black box.
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap gap-4"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
          >
            <Link to="/book-demo" className="btn-primary" data-testid="hero-book-demo-btn">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/why-responsiq" className="btn-outline" data-testid="hero-see-difference-btn">
              See the difference <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <motion.p
            className="mt-6 font-mono2 text-[10px] uppercase tracking-widest text-[#4A5A63]"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            Media at cost · 8% disclosed fee · Log-level data included
          </motion.p>
        </div>
        <motion.div
          className="lg:col-span-5"
          style={{ y, rotate }}
          initial={reduce ? false : { opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <BidderStream />
        </motion.div>
      </div>
    </section>
  );
};

const StatsStrip = () => {
  const { stats } = useContent();
  return (
    <section className="panel-dark" data-testid="stats-strip">
      <div className="container-riq py-16 lg:py-20 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
        {stats.map((s, i) => (
          <div key={i} className="border-l-2 border-[#32A2C5] pl-5">
            <p className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight text-[#F7F9FA] tabular-nums" data-testid={`stat-value-${i}`}>{s.value}</p>
            <p className="mt-3 text-sm text-[#F7F9FA]/60 leading-relaxed">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const OmnichannelShowcase = () => (
  <section className="py-20 lg:py-28" data-testid="omnichannel-showcase">
    <div className="container-riq">
      <Reveal>
        <p className="eyebrow mb-3">One workflow</p>
        <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
          Seven channels. <span className="accent-word">One</span> auction strategy.
        </h2>
        <p className="mt-4 text-[#4A5A63] max-w-2xl leading-relaxed">
          Stop stitching together point solutions. RresponsIQ plans, bids and measures across every channel from a single seat — with cross-channel frequency, unified pacing and one disclosed fee.
        </p>
      </Reveal>
      <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
        <Reveal delay={0.1}>
          <ChannelMatrix />
        </Reveal>
        <Reveal delay={0.2} className="space-y-4">
          {[
            ["Cross-channel frequency caps", "A viewer capped on CTV stays capped on mobile and audio. No more paying to annoy the same household five times."],
            ["Unified pacing & budget shifts", "Outcome AI moves budget to the channel that is actually converting — hourly, not at the end of the month."],
            ["One measurement spine", "Deduplicated reach and incrementality across all seven channels, exportable as raw logs."],
          ].map(([t, d], i) => (
            <div key={t} className="card-brut-sm card-hover p-5 flex gap-4" data-testid={`showcase-point-${i}`}>
              <span className="font-display font-black text-[#0E7A8F] text-xl leading-none">0{i + 1}</span>
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">{t}</h3>
                <p className="text-sm text-[#4A5A63] mt-1 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="py-20 lg:py-28 bg-[#EDF3F5] border-y border-[#D8E2E6]" data-testid="three-pillars">
    <div className="container-riq">
      <Reveal>
        <p className="eyebrow mb-3">All from one platform</p>
        <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          Bidder. Brain. <span className="accent-word">Receipts.</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-8">
        {[
          { num: "01", title: "A bidder built for speed", desc: "12ms median response, custom bid shading and full auction-log retention. We win the right impressions — and can show you why.", mock: <BidderStream /> },
          { num: "02", title: "Outcome AI you can inspect", desc: "Models optimize toward the KPI you define. Decision logic is documented, auditable and yours to export.", mock: <OutcomeAI /> },
          { num: "03", title: "Transparency down to the invoice", desc: "Media at cost, one disclosed fee, zero surcharges. Your invoice reads like a receipt, not a ransom note.", mock: <FeeTransparencyMock /> },
        ].map((p, i) => (
          <Reveal key={p.num} delay={i * 0.12}>
            <article className="flex flex-col h-full" data-testid={`pillar-${p.num}`}>
              <div className="mb-5">{p.mock}</div>
              <p className="font-mono2 text-xs font-bold text-[#0E7A8F] tracking-widest">{p.num}</p>
              <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight mt-1">{p.title}</h3>
              <p className="text-sm text-[#4A5A63] mt-2 leading-relaxed">{p.desc}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="py-20 lg:py-28" data-testid="how-it-works">
    <div className="container-riq">
      <Reveal>
        <p className="eyebrow mb-3">How it works</p>
        <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          Live in four <span className="accent-word">moves.</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-4 gap-6">
        {[
          ["01", "Onboard", "Connect your first-party data, define outcomes and set guardrails. Same-day for self-serve."],
          ["02", "Plan", "Build audiences and channel mixes in one workflow. Forecast reach before you spend a cent."],
          ["03", "Execute", "Outcome AI bids across all seven channels in real time, pacing budget toward what converts."],
          ["04", "Audit", "Pull log-level data anytime. Verify every impression, every hop, every dollar — yourself."],
        ].map(([n, t, d], i) => (
          <Reveal key={n} delay={i * 0.1}>
            <div className="border-t border-[#D8E2E6] pt-5 group" data-testid={`step-${n}`}>
              <p className="font-display font-black text-5xl text-[#0D0D0F]/10 group-hover:text-[#0E7A8F] transition-colors duration-300">{n}</p>
              <h3 className="font-display font-bold text-xl tracking-tight mt-2">{t}</h3>
              <p className="text-sm text-[#4A5A63] mt-2 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ClosingCTA = () => (
  <section className="panel-dark border-t border-[#D8E2E6]" data-testid="closing-cta">
    <div className="container-riq py-20 lg:py-28 text-center">
      <Reveal>
        <p className="eyebrow mb-4">Ready when you are</p>
        <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#F7F9FA] max-w-3xl mx-auto leading-[1.05]">
          See exactly where your next dollar <span className="accent-word">goes.</span>
        </h2>
        <p className="mt-5 text-[#F7F9FA]/70 max-w-xl mx-auto">
          A 30-minute walkthrough of the platform, the fee model and your log-level data. Bring your toughest questions — we brought the receipts.
        </p>
        <div className="mt-8 flex justify-center flex-wrap gap-4">
          <Link to="/book-demo" className="btn-primary" data-testid="closing-book-demo-btn">
            Book a Demo <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/pricing" className="btn-outline !bg-transparent !text-[#F7F9FA] !border-[#F7F9FA]/40 hover:!bg-[#F7F9FA]/10 hover:!text-white hover:!border-[#F7F9FA]" data-testid="closing-pricing-btn">
            View Pricing
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <StatsStrip />
      <OmnichannelShowcase />
      <Pillars />
      <HowItWorks />
      <ClosingCTA />
    </>
  );
}
